function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import { FeatureFlagApiResponse, FeatureFlagStatus, Rollout } from 'app_api/projects/feature_flags/types';
import { useFeatureFlagStore } from 'apps/feature-flags/src/store';
import { trackFeatureFlagViewPageAction, TrackingEventSource, ViewPageTrackingEvent } from 'apps/feature-flags/src/tracking';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { InputLabelReact } from 'common/components/input/label';
import { ModalReact, VerticalAlignment } from 'common/components/modal';
import { SvgIconReact } from 'common/components/svg-icon';
import { SwitchReact } from 'common/components/switch';
import { useTranslation } from 'common/i18n/hooks';
import { useFeatureFlagPageContext } from 'iron/apps/feature-flags/src/components/context';
import { FeatureFlagRollouts } from '../rollout';
import styles from './index.module.styl';

// Hide exposure stream for now, since it's not backed by any data and might change to being a chart instead.
const SHOULD_SHOW_EXPOSURE_STREAM = false;
interface Exposure {
  time: Date;
  distinctId: string;
  variant: string;
}
interface FeatureFlagPageSummaryProps {
  onUpdateFlag: (updatedFlag: FeatureFlagApiResponse) => void;
  projectId: number;
  workspaceId: number;
}
export function FeatureFlagPageSummary(_ref: FeatureFlagPageSummaryProps) {
  var _flag$ruleset$rollout, _flag$ruleset;
  let {
    onUpdateFlag,
    projectId,
    workspaceId
  }: FeatureFlagPageSummaryProps = _ref;
  const i18n = useTranslation();
  const flag = useFeatureFlagStore(store => store.currentFeatureFlag);
  const [rollouts, setRollouts] = useState<Rollout[]>(() => flag ? [...flag.ruleset.rollout] : []);
  const {
    canEdit
  } = useFeatureFlagPageContext();
  useEffect(() => {
    if (flag) {
      setRollouts([...flag.ruleset.rollout]);
    }
  }, [flag]);
  const needsUpdate = !isEqual(rollouts, (_flag$ruleset$rollout = flag === null || flag === void 0 || (_flag$ruleset = flag.ruleset) === null || _flag$ruleset === void 0 ? void 0 : _flag$ruleset.rollout) !== null && _flag$ruleset$rollout !== void 0 ? _flag$ruleset$rollout : []);
  const handleUpdate = () => {
    if (flag && needsUpdate) {
      const updatedFlag = _objectSpread(_objectSpread({}, flag), {}, {
        ruleset: _objectSpread(_objectSpread({}, flag.ruleset), {}, {
          rollout: rollouts
        })
      });
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditRollouts,
        source: TrackingEventSource.ViewPageMain,
        featureFlag: flag,
        props: {
          updatedRollouts: rollouts
        }
      });
      onUpdateFlag(updatedFlag);
    }
  };
  if (!flag) return null;
  const exposures: Exposure[] = [];
  return <div className={styles.summary} data-sentry-component="FeatureFlagPageSummary" data-sentry-source-file="index.tsx">
      <p className={styles.description}>{flag.description}</p>
      <Status flag={flag} onUpdateFlag={onUpdateFlag} data-sentry-element="Status" data-sentry-source-file="index.tsx" />
      <div className={styles.targeting}>
        <Variants flag={flag} data-sentry-element="Variants" data-sentry-source-file="index.tsx" />
        {SHOULD_SHOW_EXPOSURE_STREAM && <ExposureStream exposures={exposures} />}
      </div>
      <div>
        <FeatureFlagRollouts canUpdate projectId={projectId} workspaceId={workspaceId} rollouts={rollouts} setRollouts={setRollouts} data-sentry-element="FeatureFlagRollouts" data-sentry-source-file="index.tsx" />
      </div>

      {needsUpdate && canEdit && <div className={styles.updateButtonContainer}>
          <ButtonReact theme={ButtonTheme.Primary} onClick={handleUpdate}>
            {i18n.t("common.save")}
          </ButtonReact>
        </div>}
    </div>;
}
interface StatusProps {
  flag: FeatureFlagApiResponse;
  onUpdateFlag: (updatedFlag: FeatureFlagApiResponse) => void;
}
function Status(_ref2: StatusProps) {
  let {
    flag,
    onUpdateFlag
  }: StatusProps = _ref2;
  const i18n = useTranslation();
  const statusString = i18n.t("feature-flags.status.".concat(flag.status));
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<FeatureFlagStatus | null>(null);
  const {
    canEdit
  } = useFeatureFlagPageContext();
  const handleStatusToggle = () => {
    const newStatus: FeatureFlagStatus = flag.status === "enabled" ? "disabled" : "enabled";
    setPendingStatusChange(newStatus);
    setIsConfirmModalOpen(true);
  };
  const handleConfirmStatusChange = () => {
    if (pendingStatusChange) {
      const updatedFlag = _objectSpread(_objectSpread({}, flag), {}, {
        status: pendingStatusChange
      });
      onUpdateFlag(updatedFlag);
      setIsConfirmModalOpen(false);
      setPendingStatusChange(null);
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditStatus,
        source: TrackingEventSource.ViewPageMain,
        featureFlag: flag,
        props: {
          updatedStatus: pendingStatusChange
        }
      });
    }
  };
  const handleCancelStatusChange = () => {
    setIsConfirmModalOpen(false);
    setPendingStatusChange(null);
  };
  return <>
      <Card className={styles.cardGray} data-sentry-element="Card" data-sentry-source-file="index.tsx">
        <div className={styles.statusLeft}>
          <SvgIconReact icon='thumbs-up' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
          <div>
            <p className={styles.cardTitle}>{i18n.t("feature-flags.page.flag-status", {
              status: statusString
            })}</p>
            {/* <p className={styles.usersServed}>{i18n.t(`feature-flags.page.serving-users`, {count: 122})}</p> */}
          </div>
        </div>
        <div className={styles.statusToggle}>
          <InputLabelReact className={styles.statusLabel} style-override='.mp-input-label-container label.mp-input-label { color: var(--gray5); }' label={statusString} helpText={i18n.t("feature-flags.page.status-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
          <SwitchReact disabled={!canEdit} on={flag.status === "enabled"} onChange={handleStatusToggle} data-sentry-element="SwitchReact" data-sentry-source-file="index.tsx" />
        </div>
      </Card>

      <ModalReact open={isConfirmModalOpen} growToContent closeable verticalAlignment={VerticalAlignment.Top} data-sentry-element="ModalReact" data-sentry-source-file="index.tsx">
        <div className={styles.confirmModal}>
          <h3 className={styles.confirmTitle}>
            {pendingStatusChange === "enabled" ? i18n.t("feature-flags.page.confirm.enable.title") : i18n.t("feature-flags.page.confirm.disable.title")}
          </h3>
          <p className={styles.confirmDescription}>
            {pendingStatusChange === "enabled" ? i18n.t("feature-flags.page.confirm.enable.description", {
            flagName: flag.name
          }) : i18n.t("feature-flags.page.confirm.disable.description", {
            flagName: flag.name
          })}
          </p>
          <div className={styles.confirmButtons}>
            <ButtonReact theme={ButtonTheme.Secondary} onClick={handleCancelStatusChange} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              {i18n.t("common.cancel")}
            </ButtonReact>
            <ButtonReact theme={ButtonTheme.Primary} onClick={handleConfirmStatusChange} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              {pendingStatusChange === "enabled" ? i18n.t("feature-flags.page.confirm.enable.confirm") : i18n.t("feature-flags.page.confirm.disable.confirm")}
            </ButtonReact>
          </div>
        </div>
      </ModalReact>
    </>;
}
interface VariantsProps {
  flag: FeatureFlagApiResponse;
}
function Variants(_ref3: VariantsProps) {
  let {
    flag
  }: VariantsProps = _ref3;
  const i18n = useTranslation();
  const variants = flag.ruleset.variants.map(variant => ({
    name: variant.key,
    split: variant.split
  }));
  const header = <p className={styles.cardTitle}>{i18n.t("feature-flags.page.variants.title")}</p>;
  return <>
      <Card className={styles.cardGray} header={header} data-sentry-element="Card" data-sentry-source-file="index.tsx">
        <table className={styles.targetingTable}>
          <thead>
            <tr>
              <th className={styles.columnHeader}>{i18n.t("feature-flags.page.variants.name")}</th>
              <th className={styles.columnHeader}>{i18n.t("feature-flags.page.variants.split")}</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((variant, index) => <tr className={styles.row} key={index}>
                <td className={styles.cell}>{variant.name}</td>
                <td className={styles.cell}>{variant.split * 100}%</td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </>;
}
interface ExposureStreamProps {
  exposures: Exposure[];
}
function ExposureStream(_ref4: ExposureStreamProps) {
  let {
    exposures
  }: ExposureStreamProps = _ref4;
  const i18n = useTranslation();
  const header = <div className={styles.exposureHeader}>
      <p className={styles.cardTitle}>{i18n.t("feature-flags.page.exposure-stream.title")}</p>
      <ButtonReact size={ButtonSize.Small} theme={ButtonTheme.Secondary}>
        {i18n.t("feature-flags.page.exposure-stream.see-all")}
      </ButtonReact>
    </div>;
  return <Card className={styles.cardGray} header={header} data-sentry-element="Card" data-sentry-component="ExposureStream" data-sentry-source-file="index.tsx">
      <table className={styles.targetingTable}>
        <thead>
          <tr>
            <th className={styles.columnHeader}>{i18n.t("feature-flags.page.exposure-stream.time")}</th>
            <th className={styles.columnHeader}>{i18n.t("feature-flags.page.exposure-stream.distinct-id")}</th>
            <th className={styles.columnHeader}>{i18n.t("feature-flags.page.exposure-stream.variant")}</th>
          </tr>
        </thead>
        <tbody>
          {exposures.map((exposure, index) => <tr className={styles.row} key={index}>
              <td className={styles.cell}>{i18n.timeFromNow(exposure.time)}</td>
              <td className={styles.cell}>{exposure.distinctId}</td>
              <td className={styles.cell}>{exposure.variant}</td>
            </tr>)}
        </tbody>
      </table>
    </Card>;
}
interface CardProps {
  children: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
}
function Card(_ref5: CardProps) {
  let {
    children,
    className,
    header
  }: CardProps = _ref5;
  if (!header) {
    return <div className={clsx(styles.card, styles.cardWithoutHeader, className)}>{children}</div>;
  } else {
    return <div className={clsx(styles.card, styles.cardWithHeader, className)}>
        <div className={styles.cardHeader}>{header}</div>
        <div className={styles.cardBody}>{children}</div>
      </div>;
  }
}